import client from "services/ngage/api-client";
import DataPage from "types/DataPage";
import {Membership, MembershipData} from "types/Membership";
import deserializeMembership from "services/ngage/deserializers/membership";
import {parameters, queryString} from "services/string";
import {snakeKeys} from "../array";
import DataResource from "../../types/DataResource";

type MembershipFilters = {
    search?: string | null
    member?: number
    sort?: string | null
    page?: number
    perPage?: number
    owner?: number
}

/**
 * Get all memberships (paginated)
 */
const all = async ({member, page = 1, perPage = 100}: MembershipFilters) => {
    const query = queryString({member, page, per_page: perPage})
    const response = await client.get<DataPage<Membership> | null>(`/memberships?${query}`)

    if (response.data) {
        response.data.data = response.data.data.map(deserializeMembership)
    }

    return response.data
}

/**
 * Get all memberships that match the given filters (paginated)
 */
const filtered = async (filters: MembershipFilters = {}) => {
    const params = parameters(filters, {page: 1, per_page: 100});
    const response = await client.get<DataResource<Membership> | null>('/memberships', {params});

    if (response.data) {
        response.data.data = response.data.data.map(deserializeMembership)
    }

    return response.data
}

/**
 * Get a single membership
 *
 * @param {number} id
 */
const get = async (id: number) => {
    return deserializeMembership(
        (await client.get<Membership>(`/memberships/${id}`)).data
    )
}

/**
 * Store a new membership 
 *
 * @param data
 */
const store = async (data: MembershipData) => {
    return deserializeMembership(
        (await client.post<Membership>('/memberships', data)).data
    )
}

/**
 * Update an existing membership 
 *
 * @param {number} id
 * @param data
 */
const update = async (id: number, data: MembershipData) => {
    return deserializeMembership(
        (await client.put<Membership>(`/memberships/${id}`, data)).data
    )
}

/**
 * Permanently delete a membership
 *
 * @param {number} id
 */
const destroy = async (id: number) => {
    return (await client.delete<null>(`/memberships/${id}`)).status === 204
}

const memberships = {
    all,
    filtered,
    get,
    store,
    update,
    destroy
}

export default memberships;